import { useCallback, useSyncExternalStore } from 'react';

const STORAGE_KEY = 'b1link:onboarding-layout-v2';

/**
 * Read the raw flag from localStorage (non-reactive).
 * Useful in contexts that can't call hooks (e.g. root.tsx computed values).
 */
export function getOnboardingLayoutFlag(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) !== 'legacy';
  } catch {
    return true; // default to new layout
  }
}

// ── Tiny pub/sub so every hook instance re-renders on toggle ──
const listeners = new Set<() => void>();
function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}
function getSnapshot(): boolean {
  return getOnboardingLayoutFlag();
}

/**
 * Feature toggle for the onboarding layout.
 *
 * - `useSidebarLayout === true`  → new vertical sidebar layout (default)
 * - `useSidebarLayout === false` → legacy horizontal stepper layout
 *
 * Backed by `localStorage` so the choice persists across reloads.
 * All mounted hook instances re-render together via `useSyncExternalStore`.
 */
export function useOnboardingLayoutToggle() {
  const useSidebarLayout = useSyncExternalStore(subscribe, getSnapshot, () => true);

  const toggle = useCallback(() => {
    const next = !getOnboardingLayoutFlag();
    try {
      if (next) {
        localStorage.removeItem(STORAGE_KEY);
      } else {
        localStorage.setItem(STORAGE_KEY, 'legacy');
      }
    } catch {
      // storage unavailable — silently ignore
    }
    listeners.forEach((cb) => cb());
  }, []);

  return { useSidebarLayout, toggle } as const;
}
